import type { SagaAuditLogDto } from '@/dtos/ErrorHandlingSaga'
import { Badge, Box } from '@chakra-ui/react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type Edge, type EdgeProps } from '@xyflow/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { AuditStepList } from './AuditStepList'
import { ADJACENT_CURVATURE } from './edgeStyle'

// A `type` (not `interface`) so it satisfies React Flow's `Record<string, unknown>` edge-data constraint without an index signature.
export type AuditEdgeData = {
  label?: string
  // Audit-log rows whose PreviousState→NewState matches this edge's source→target.
  steps: SagaAuditLogDto[]
  // Bézier curvature (0 = straight, ~0.5 = pronounced S); tuned per edge in buildFlow — skips curve harder.
  curvature?: number
}

export type AuditFlowEdge = Edge<AuditEdgeData, 'auditEdge'>

/**
 * Shared edge label: a solid white pill (masking the line behind it) with the optional transition label plus, when
 * audit rows exist, a coloured count badge that reveals the attempts in a hover tooltip. The pill chrome is written
 * once and reused — wrapped in a Tooltip only when there are steps. Used by both the forward AuditEdge and SelfLoopEdge.
 */
export const EdgeLabel = ({ steps }: { steps: SagaAuditLogDto[] }) => {
  const hasSteps = steps.length > 0
  if (!hasSteps) return null

  return (
    <CustomTooltip
      hasArrow
      trigger='clickHover'
      placement="top"
      bg="white"
      color="gray.800"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="md"
      p={0}
      openDelay={80}
      label={<AuditStepList steps={steps} />}
    >
      <Badge variant='solid' colorScheme={steps.some(s => !s.success) ? 'red' : 'green'} borderRadius="full">{steps.length}</Badge>
    </CustomTooltip>
  )
}

/**
 * Cubic-Bézier edge: the curve leaves the source and enters the target along its handle direction (Left/Right for the
 * cross-column backbone, Top/Bottom for same-column drops). At the apex it renders the label + hover attempts.
 */
export const AuditEdge = ({
  id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, style, markerEnd, data,
}: EdgeProps<AuditFlowEdge>) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: data?.curvature ?? ADJACENT_CURVATURE,
  })
  const steps = data?.steps ?? []
  const label = data?.label

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {(label || steps.length > 0) && (
        <EdgeLabelRenderer>
          <Box
            className="nodrag nopan"
            position="absolute"
            transform={`translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`}
            pointerEvents="all"
            zIndex={5}
          >
            <EdgeLabel steps={steps} />
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
